import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import StashitLogo from '../components/Logo/StashitLogo';

const AdminAssets: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'logo' | 'letterhead'>('logo');

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-primary-700 mb-4">Access Denied</h1>
          <p className="text-primary-600">You need admin privileges to access this page.</p>
          <Link to="/" className="btn-primary mt-4 inline-block">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const downloadSVG = () => {
    const svgContent = document.querySelector('#logo-svg')?.outerHTML;
    if (svgContent) {
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'StashitLogo.svg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

    const downloadPNG = () => {
    try {
      const svg = document.querySelector('#logo-svg svg');
      if (!svg) {
        alert('Logo not found. Please try again.');
        return;
      }

      // Method 1: Try using canvas with SVG data URL
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));

      const img = new Image();
      img.onload = function() {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            // Fallback to data URL method
            fallbackPNGDownload();
            return;
          }

          canvas.width = 800;
          canvas.height = 240;

          // Clear canvas and draw image
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Convert to PNG and download
          canvas.toBlob((blob) => {
            if (blob) {
              const downloadUrl = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = downloadUrl;
              link.download = 'StashitLogo.png';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(downloadUrl);
            } else {
              // Fallback to data URL method
              fallbackPNGDownload();
            }
          }, 'image/png');
        } catch (error) {
          console.error('Error with canvas method:', error);
          // Fallback to data URL method
          fallbackPNGDownload();
        }
      };

      img.onerror = function() {
        // Fallback to data URL method
        fallbackPNGDownload();
      };

      img.src = svgDataUrl;
    } catch (error) {
      console.error('Error in downloadPNG:', error);
      alert('An error occurred while downloading PNG. Please try again.');
    }
  };

  // Fallback method using data URL
  const fallbackPNGDownload = () => {
    try {
      const svg = document.querySelector('#logo-svg svg');
      if (!svg) {
        alert('Logo not found. Please try again.');
        return;
      }

      // Create a temporary link with SVG data URL
      const svgData = new XMLSerializer().serializeToString(svg);
      const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));

      const link = document.createElement('a');
      link.href = svgDataUrl;
      link.download = 'StashitLogo.svg';
      link.textContent = 'Download SVG instead';

      // Show a message and offer SVG download
      if (confirm('PNG conversion failed. Would you like to download the SVG file instead? You can then convert it to PNG using any image editor.')) {
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error in fallback method:', error);
      alert('Download failed. Please try downloading the SVG file instead.');
    }
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-primary-900">Logo & Assets Management</h1>
              <p className="mt-2 text-primary-600">
                Download and manage your Stashit brand assets
              </p>
            </div>
            <Link
              to="/admin"
              className="btn-secondary"
            >
              ← Back to Admin
            </Link>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-neutral-100 p-1 rounded-lg mb-8">
          <button
            onClick={() => setActiveTab('logo')}
            className={`flex-1 py-3 px-6 rounded-md font-medium transition-colors ${
              activeTab === 'logo'
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-800'
            }`}
          >
            🎨 Logo Assets
          </button>
          <button
            onClick={() => setActiveTab('letterhead')}
            className={`flex-1 py-3 px-6 rounded-md font-medium transition-colors ${
              activeTab === 'letterhead'
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-800'
            }`}
          >
            📄 Letterhead Template
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'logo' && (
          <div className="space-y-8">
            {/* Logo Preview */}
            <div className="card-primary">
              <h3 className="section-header mb-6">Logo Preview</h3>
              <div className="flex justify-center mb-6">
                <div id="logo-svg">
                  <StashitLogo width={400} height={120} showTagline={true} />
                </div>
              </div>
              <div className="text-center text-sm text-primary-600">
                <p>Your official Stashit logo with tagline "Secure • Simple • Smart Storage"</p>
              </div>
            </div>

            {/* Download Options */}
            <div className="card-secondary">
              <h3 className="section-header secondary mb-6">Download Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-6 border border-secondary-200 rounded-lg text-center">
                  <div className="text-4xl mb-3">📐</div>
                  <h4 className="font-semibold text-secondary-800 mb-2">SVG Format</h4>
                  <p className="text-sm text-secondary-600 mb-4">
                    Best for digital use and print. Scalable to any size without quality loss.
                  </p>
                  <button
                    onClick={downloadSVG}
                    className="btn-primary w-full"
                  >
                    Download SVG
                  </button>
                </div>

                            <div className="p-6 border border-secondary-200 rounded-lg text-center">
              <div className="text-4xl mb-3">🖼️</div>
              <h4 className="font-semibold text-secondary-800 mb-2">PNG Format</h4>
              <p className="text-sm text-secondary-600 mb-4">
                High-resolution image format perfect for web use and basic printing.
              </p>
              <button
                onClick={downloadPNG}
                className="btn-secondary w-full"
              >
                Download PNG
              </button>
              <p className="text-xs text-secondary-500 mt-2">
                If PNG fails, you'll be offered SVG download instead
              </p>
            </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'letterhead' && (
          <div className="space-y-8">
            {/* Letterhead Preview */}
            <div className="card-primary">
              <h3 className="section-header mb-6">Letterhead Preview</h3>
              <div className="border border-neutral-200 rounded-lg p-8 bg-white">
                <div className="max-w-2xl mx-auto">
                  {/* Header */}
                  <div className="border-b-3 border-primary-600 pb-6 mb-6 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <StashitLogo width={60} height={45} showTagline={false} />
                      <div>
                        <h2 className="text-2xl font-bold text-primary-700">Stashit</h2>
                        <p className="text-sm text-neutral-500">Secure • Simple • Smart Storage</p>
                      </div>
                    </div>
                    <div className="text-right text-xs text-neutral-500">
                      <p><strong>Email:</strong> chasethebanker101@gmail.com</p>
                      <p><strong>Cell:</strong> +267 74365627</p>
                      <p><strong>Botswana</strong></p>
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className="min-h-64 border-2 border-dashed border-neutral-300 rounded-lg flex items-center justify-center text-neutral-400 italic">
                    <p>Your letter content goes here...</p>
                  </div>

                  {/* Footer */}
                  <div className="border-t-2 border-neutral-200 pt-4 mt-6 text-xs text-neutral-500 flex justify-between">
                    <div>
                      <strong>Email:</strong> chasethebanker101@gmail.com |
                      <strong>Cell:</strong> +267 74365627 |
                      <strong>Location:</strong> Botswana
                    </div>
                    <div className="text-right">
                      <strong>Stashit</strong> - Secure Storage Solutions
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Download Letterhead */}
            <div className="card-secondary">
              <h3 className="section-header secondary mb-6">Download Letterhead</h3>
              <div className="text-center">
                <p className="text-secondary-600 mb-6">
                  Download the professional letterhead template with your Stashit logo and contact information.
                  Perfect for business correspondence and official documents.
                </p>
                <a
                  href="/assets/StashitLetterhead.html"
                  download
                  className="btn-primary text-lg px-8 py-4 inline-block"
                >
                  📄 Download Letterhead Template
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAssets;
